import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../hooks/useAuth";

// Define the types for our context
interface AddressData {
  street_address?: string;
  apartment?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  is_default?: boolean;
  phone_number?: string;
  latitude?: number;
  longitude?: number;
  [key: string]: any; // Allow for additional properties
}

interface DeliveryAddressContextType {
  selectedAddress: AddressData | null;
  formattedAddress: string;
  updateAddress: (addressData: AddressData | null) => Promise<void>;
  fetchUserAddress: () => Promise<void>;
}

export const DeliveryAddressContext =
  createContext<DeliveryAddressContextType | null>(null);

export function DeliveryAddressProvider({ children }) {
  const { user } = useAuth();
  const [selectedAddress, setSelectedAddress] = useState<AddressData | null>(
    null
  );
  const [formattedAddress, setFormattedAddress] = useState(
    "Set Delivery Address"
  );

  // Add console logs to debug
  useEffect(() => {
    console.log("Address Context - User changed:", user?.id);
    if (user) {
      fetchUserAddress();
    } else {
      // Clear address when user signs out
      setSelectedAddress(null);
      setFormattedAddress("Set Delivery Address");
    }
  }, [user]);

  const formatAddress = (addressData: AddressData): string => {
    if (!addressData?.street_address) {
      return "Set Delivery Address";
    }

    let formatted = addressData.street_address;

    // Add apartment if available
    if (addressData.apartment) {
      formatted += ` ${addressData.apartment}`;
    }

    // Add city, state, zip
    const cityStateZip = [
      addressData.city,
      addressData.state,
      addressData.zip_code,
    ]
      .filter(Boolean)
      .join(", ");

    if (cityStateZip) {
      formatted += `, ${cityStateZip}`;
    }

    return formatted;
  };

  const fetchUserAddress = async () => {
    if (!user) return;

    try {
      const { data: addressData, error: addressError } = await supabase
        .from("delivery_addresses")
        .select("*")
        .eq("user_id", user.id)
        .eq("is_default", true)
        .single();

      // If no delivery address, try profile
      if (!addressData) {
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select(
            "street_address, apartment, city, state, zip_code, phone_number"
          )
          .eq("id", user.id)
          .single();

        console.log("Profile data for address:", profileData);

        if (profileData?.street_address) {
          const address: AddressData = {
            ...profileData,
            is_default: true,
          };
          setSelectedAddress(address);
          setFormattedAddress(formatAddress(address));
          return;
        } else {
          // No address set
          setSelectedAddress(null);
          setFormattedAddress("Set Delivery Address");
          return;
        }
      }

      console.log("Fetched address data:", addressData, "Error:", addressError);

      if (addressData) {
        setSelectedAddress(addressData);
        setFormattedAddress(formatAddress(addressData));
      } else {
        // No address set
        setSelectedAddress(null);
        setFormattedAddress("Set Delivery Address");
      }
    } catch (error) {
      console.error("Error fetching address:", error);
      // Error case - reset to default
      setSelectedAddress(null);
      setFormattedAddress("Set Delivery Address");
    }
  };

  const updateAddress = async (addressData: AddressData | null) => {
    if (!addressData || !addressData.street_address) {
      setSelectedAddress(null);
      setFormattedAddress("Set Delivery Address");
      console.log("Cleared address");
      return;
    }

    setSelectedAddress(addressData);
    setFormattedAddress(formatAddress(addressData));
    console.log(
      "Updated address:",
      addressData,
      "Formatted:",
      formatAddress(addressData)
    );
  };

  return (
    <DeliveryAddressContext.Provider
      value={{
        selectedAddress,
        formattedAddress,
        updateAddress,
        fetchUserAddress,
      }}
    >
      {children}
    </DeliveryAddressContext.Provider>
  );
}

export function useDeliveryAddressContext() {
  const context = useContext(DeliveryAddressContext);
  if (!context) {
    throw new Error(
      "useDeliveryAddressContext must be used within a DeliveryAddressProvider"
    );
  }
  return context;
}
